import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio, { RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ToastBody, ToastStyle } from "@waytek/MobiusOverrides/Toast/Toast";
import { CheckoutShippingFormContext } from "@waytek/Overrides/Pages/CheckoutShippingPage";
import setCheckoutOrderType from "@waytek/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetCheckoutOrderType";
import { CustomState } from "@waytek/Store/Reducers";
import { colorStrings, mediaQueriesBP } from "@waytek/Utilities/variables";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    const cart = getCurrentCartState(state).value;
    const shippingOrderType = cart?.properties ? cart?.properties["shippingOrderType"] : "";

    return { cart, shippingOrderType, defaultCondition: state.checkoutShipping.condition };
};

const mapDispatchToProps = {
    setCheckoutOrderType,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingAddressesStyles {
    container?: GridContainerProps;
    containerTitleGridItem?: GridItemProps;
    titleWrapper?: InjectableCss;
    headlineText?: TypographyProps;
    orderTypeGridItem?: GridItemProps;
    orderTypeTextLine?: InjectableCss;
    orderTypeText?: TypographyProps;
    orderNotesGridItem?: GridItemProps;
    messageWrapper?: InjectableCss;
    orderTypeRadioGroup?: RadioGroupProps;
    orderTypeErrorText?: TypographyProps;
}

export const checkoutShippingAddressesStyles: CheckoutShippingAddressesStyles = {
    container: { gap: 20 },
    containerTitleGridItem: { width: 12 },
    titleWrapper: {
        css: css`
            border-bottom: 3px solid ${getColor(colorStrings.commonGlobalIndustrialTaupe)};
            padding-bottom: 4px;
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    headlineText: {
        variant: "h5",
        forwardAs: "h3",
        transform: "none",
        size: 18,
        lineHeight: 1.78,
        weight: 700,
        css: css`
            padding: 0;
            margin: 0;

            @media print {
                font-size: 15px;
            }
        `,
    },
    orderTypeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;

            ${mediaQueriesBP.maxLG} {
                padding-bottom: 1px;
            }
        `,
    },
    orderTypeTextLine: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    orderTypeText: {
        transform: "none",
        size: 14,
        lineHeight: 1.71,
        weight: "bold",
        css: css`
            padding: 0;
            margin: 0;
        `,
    },
    orderNotesGridItem: { width: [12, 12, 12, 6, 6] },
    messageWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            padding: 0;
            width: 100%;
            max-width: 100%;
            box-shadow: none;

            & > div {
                box-shadow: none;

                ${mediaQueriesBP.maxMD} {
                    width: 100%;
                    max-width: 100%;
                    margin-left: 0;
                }

                ${mediaQueriesBP.minMD} {
                    display: none;
                }
            }
        `,
    },
    orderTypeRadioGroup: {
        labelProps: {
            css: css`
                display: none;
            `,
        },
        errorProps: {
            css: css`
                display: none;
            `,
        },
        css: css`
            ${RadioStyle} {
                margin-top: 0;
                margin-right: 43px;

                &:last-of-type {
                    margin-right: 0;
                }

                input {
                    margin: 0;
                }
            }
        `,
    },
    orderTypeErrorText: {
        size: "12px",
        lineHeight: "17px",
        color: colorStrings.commonGlobalErrorRed,
    },
};

const styles = checkoutShippingAddressesStyles;

const CheckoutShippingShippingInformation: FC<Props> = ({
    id,
    cart,
    setCheckoutOrderType,
    shippingOrderType,
    defaultCondition,
}) => {
    if (!cart) {
        return null;
    }

    const [orderType, setOrderType] = useState(shippingOrderType);
    const [showOutsideState, setShowOutsideState] = React.useState(defaultCondition);
    const pageContext = useContext(CheckoutShippingFormContext);

    useEffect(() => {
        setShowOutsideState(defaultCondition);
    }, [defaultCondition]);

    useEffect(() => {
        pageContext.validators.orderType = () =>
            new Promise((resolve, reject) => {
                resolve(orderType != null && orderType !== "");
            });
    });

    const orderTypeChange = (event: any) => {
        setOrderType(event.target.value);
        setCheckoutOrderType({ shippingOrderType: event.target.value });
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem width={[12, 12, 0, 0, 0]}>
                <StyledWrapper>
                    <StyledWrapper {...styles.messageWrapper}>
                        <ToastStyle
                            transitionState="entered"
                            transitionLength={0}
                            messageType="warning"
                            data-test-selector="AccountSettings_Message"
                        >
                            <ToastBody data-id="toast-body">
                                <Typography>{siteMessage("CheckoutShipping_OutsideUS")}</Typography>
                            </ToastBody>
                        </ToastStyle>
                    </StyledWrapper>
                    {showOutsideState && (
                        <StyledWrapper {...styles.messageWrapper} style={{ marginTop: 20 }}>
                            <ToastStyle
                                transitionState="entered"
                                transitionLength={0}
                                messageType="warning"
                                data-test-selector="AccountSettings_Message"
                            >
                                <ToastBody data-id="toast-body">
                                    {siteMessage("CheckoutShipping_NotAvailableState")}
                                </ToastBody>
                            </ToastStyle>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.containerTitleGridItem}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.headlineText}>{translate("Shipping Information")}</Typography>
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.orderTypeGridItem}>
                <StyledWrapper {...styles.orderTypeTextLine}>
                    <Typography {...styles.orderTypeText}>{translate("Order Type")}</Typography>
                </StyledWrapper>
                <StyledWrapper className="FormFieldStyle__RadioGroup">
                    <RadioGroup
                        {...styles.orderTypeRadioGroup}
                        value={orderType}
                        onChangeHandler={orderTypeChange}
                        horizontal={true}
                        aria-invalid={orderType == null}
                    >
                        <Radio value="personal" data-test-selector="checkoutShipping_orderType_PersonalUse">
                            {translate("Personal Use")}
                        </Radio>
                        <Radio value="business" data-test-selector="checkoutShipping_orderType_BusinessUse">
                            {translate("Business Use")}
                        </Radio>
                    </RadioGroup>
                </StyledWrapper>
                {pageContext.formSubmitAttempted && orderType == null && (
                    <StyledWrapper>
                        <Typography {...styles.orderTypeErrorText}>
                            {siteMessage("CheckoutShipping_OrderTypeError")}
                        </Typography>
                    </StyledWrapper>
                )}
            </GridItem>

            <GridItem {...styles.orderNotesGridItem}>
                <Zone contentId={id} zoneName="CheckoutShippingOrderNotes"></Zone>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutShippingShippingInformation),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage"],
        displayName: "Shipping Information",
    },
};

export default widgetModule;
